import type { FormEventHandler } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { formToObject } from 'lib/forms/formToObject'
import { updateAddPhonePromptedAt, updateUserDetails } from 'actions/AuthActions'
import TextButton from 'LuxKit/Button/TextButton'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import PhoneInput from 'components/Common/Form/Input/PhoneInput'
import LegalText from './AccountAccessLegalText'
import Group from 'components/utils/Group'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import type { AccountAccessModalResult } from './AccountAccessModal.types'
import { trackClientEvent } from 'analytics/analytics'
import useExperiment from 'hooks/Optimizely/useExperiment'
import { Experiments } from 'constants/optimizely'
import { PHONE_POINTS_CONFIG } from 'constants/experimentConfig'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import StackedLoyaltyTierIcon from 'LuxKit/Icons/loyalty/StackedLoyaltyTierIcon'

interface PhoneNum {
  phone: string
  prefix: string
}

interface AddPhoneForm {
  phoneNum: PhoneNum
}

interface Props {
  error?: any
  regionCode?: string
  phone?: string
  dismissable?: boolean
  trackPromptImpression?: boolean
  hasDismissed?: boolean
  onDismissChange?: (dismissed: boolean) => void
}

const ADD_PHONE_OPTIMIZELY_EVENT = {
  id: '4586532951031808',
  key: 'add_phone_number',
} as const

function AccountAccessAddPhone(props: Props) {
  const {
    regionCode,
    phone,
    dismissable,
    trackPromptImpression = true,
    hasDismissed: hasDismissedProp,
    onDismissChange,
  } = props

  const intl = useIntl()
  const termsCheckboxRef = useRef<HTMLInputElement>(null)
  const modalContext = useModalElementContext<AccountAccessModalResult>()
  const dispatch = useAppDispatch()
  const [internalHasDismissed, setInternalHasDismissed] = useState(false)
  const isDismissedControlled = typeof hasDismissedProp === 'boolean'
  const hasDismissed = isDismissedControlled ? Boolean(hasDismissedProp) : internalHasDismissed

  const isLoyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const { variation } = useExperiment(Experiments.croPhoneNumberSocietePoints, { canActivate: isLoyaltyEnabled })

  const phoneConfig = variation && PHONE_POINTS_CONFIG[variation]
    ? PHONE_POINTS_CONFIG[variation]
    : PHONE_POINTS_CONFIG.control

  const pointsReward = phoneConfig.points
  const phonePointsEarnKey = phoneConfig.earnKey
  const formattedPointsReward = intl.formatNumber(pointsReward)

  const showPointsEarnCallout = isLoyaltyEnabled && pointsReward > 0

  const setHasDismissed = useCallback((nextState: boolean) => {
    if (!isDismissedControlled) {
      setInternalHasDismissed(nextState)
    }
    onDismissChange?.(nextState)
  }, [isDismissedControlled, onDismissChange])

  const onAddPhone = useCallback<FormEventHandler<HTMLFormElement>>((event) => {
    event.preventDefault()
    const joinData = formToObject<AddPhoneForm>(event.currentTarget)
    dispatch(updateUserDetails(
      {
        phone: joinData.phoneNum.phone,
        phonePrefix: joinData.phoneNum.prefix,
      },
      null,
      isLoyaltyEnabled ? phonePointsEarnKey : undefined,
    ))
  }, [dispatch, isLoyaltyEnabled, phonePointsEarnKey])

  const onClose = useCallback(() => {
    if (hasDismissed) {
      return modalContext.resolve({ loggedIn: true })
    }

    setHasDismissed(true)
  }, [modalContext, hasDismissed, setHasDismissed])

  useEffect(() => {
    if (trackPromptImpression) {
      dispatch(updateAddPhonePromptedAt(new Date()))
    }
    trackClientEvent({
      subject: 'add_phone_number',
      action: 'impression',
      category: 'logging',
      type: 'operational',
    })
  }, [dispatch, trackPromptImpression])

  useEffect(() => {
    if (phone?.length) {
      const optimizelyEvent = {
        optimizelyEventId: ADD_PHONE_OPTIMIZELY_EVENT.id,
        optimizelyEventKey: ADD_PHONE_OPTIMIZELY_EVENT.key,
      }

      trackClientEvent({
        subject: 'add_phone_number',
        action: 'interaction',
        category: 'logging',
        type: 'operational',
        ...optimizelyEvent,
      })
      modalContext.resolve({ loggedIn: true })
    }
  }, [modalContext, phone])

  let title: string
  if (hasDismissed) {
    title = intl.formatMessage({
      id: 'VCyRccvh4C61jdcUdzqs',
      defaultMessage: 'Are you sure?',
      description: 'Modal title when user tries to dismiss the add phone number prompt',
    })
  } else if (showPointsEarnCallout) {
    title = intl.formatMessage({
      id: 'CVIQjDUfhdtiPZW7V2Pe',
      defaultMessage: 'Secure your account with a mobile number and earn {points} points',
      description: 'Modal title with points reward for adding phone number',
    }, { points: formattedPointsReward })
  } else {
    title = intl.formatMessage({
      id: 'wviA6ESdOgBP49KT65RG',
      defaultMessage: 'Secure your account with a mobile number',
      description: 'Modal title for adding phone number to account in the account access section',
    })
  }

  const subtitleText = intl.formatMessage({
    id: '06j1MVhGhMK9B3Yftmdi',
    defaultMessage: 'Link your mobile to your account for added security and a smoother, faster login experience.',
    description: 'Subtitle explaining benefits of adding a phone number',
  })

  const warningText = intl.formatMessage({
    id: '4zdgDkj0smvGUStmyHH7',
    defaultMessage: 'Adding your phone number is highly recommended to help keep your account safe',
    description: 'Warning message when user tries to skip adding phone number',
  })

  const phoneLabel = intl.formatMessage({
    id: 'T_4RhGB0-sNJgnkOJhK2',
    defaultMessage: 'Phone number',
    description: 'Form field label for phone number input in add phone prompt',
  })

  const secureMyAccountLabel = intl.formatMessage({
    id: 'gOcKbwOUPcWnJ1cX6lj4',
    defaultMessage: 'Secure my account',
    description: 'Button text to add phone number after initial dismissal',
  })

  const addMobileLabel = intl.formatMessage({
    id: 'UuTr4xCfmW2QcmgjI2D2',
    defaultMessage: 'Add mobile number',
    description: 'Primary button text to add mobile number to account',
  })

  return (
    <>
      <ModalHeader
        title={title}
        subtitle={!hasDismissed ? subtitleText : undefined}
        dismissible={dismissable}
        onCloseButtonClick={onClose}
      />
      <ModalBody>
        <ModalContent>
          <form name="joinForm" onSubmit={onAddPhone}>
            <Group direction="vertical" gap={12}>
              {hasDismissed && <MessageBanner kind="warning" icon={<LineInfoCircleIcon />}>
                {warningText}
              </MessageBanner>}
              <Group direction="vertical" gap={4}>
                <PhoneInput
                  id="phoneNum"
                  name="phoneNum"
                  label={phoneLabel}
                  prefixCountry={regionCode}
                  required
                />
                {showPointsEarnCallout && !hasDismissed && (
                  <CaptionTextBlock
                    variant="medium"
                    colour="neutral-three"
                    startIcon={<StackedLoyaltyTierIcon />}
                  >
                    {intl.formatMessage({
                      id: 'GYZyb9HQpxShy0YF0z-9',
                      defaultMessage: 'Earn {points} points for adding your phone number',
                      description: 'Points incentive text shown below phone input when A/B test variant awards points',
                    }, { points: formattedPointsReward })}
                  </CaptionTextBlock>
                )}
              </Group>
              <CheckboxInput
                ref={termsCheckboxRef}
                name="agreeTandC"
                data-testid="terms-and-privacy-agree-checkbox"
                required
              >
                <LegalText modalType="promptAddPhone" />
              </CheckboxInput>
              <TextButton
                kind="primary"
                fit="flex"
                type="submit"
                size="large"
              >
                {hasDismissed ? secureMyAccountLabel : addMobileLabel}
              </TextButton>
            </Group>
          </form>
        </ModalContent>
      </ModalBody>
    </>
  )
}

function mapStateToProps(state: App.State) {
  return {
    error: state.auth.error,
    regionCode: state.geo.currentRegionCode,
    phone: state.auth.account.phone,
  }
}

export default connect(mapStateToProps)(AccountAccessAddPhone)
